/**
 * Enhanced input validation and sanitization
 * Implements OWASP input validation security standards
 */

import { z } from 'zod';
import { sanitizeInput, SECURITY_CONFIG } from './security';

// NOTE: Using RegExp constructor here avoids ESLint false-positives with regex literal escaping.
const PASSWORD_SYMBOL_REGEX = new RegExp('[!@#$%^&*()_+\\-=\\[\\]{};:\'"\\\\|,.<>/?]');

/**
 * Common validation schemas for form inputs
 */
export const validationSchemas = {
  // Email validation with security checks
  email: z.string()
    .min(1, 'Email is required')
    .max(254, 'Email address too long')
    .email('Invalid email format')
    .refine((email) => {
      // Check for suspicious patterns
      return !email.includes('..') && 
             !email.startsWith('.') && 
             !email.endsWith('.') &&
             !email.includes('+') || email.split('@')[0].includes('+');
    }, 'Invalid email format')
    .transform(sanitizeInput),

  // Password validation with OWASP requirements
  password: z.string()
    .min(SECURITY_CONFIG.PASSWORD_MIN_LENGTH, `Password must be at least ${SECURITY_CONFIG.PASSWORD_MIN_LENGTH} characters`)
    .max(128, 'Password too long')
    .refine((pwd) => {
      // Check for uppercase letter
      if (SECURITY_CONFIG.PASSWORD_REQUIRE_UPPERCASE && !/[A-Z]/.test(pwd)) {
        return false;
      }
      // Check for lowercase letter
      if (SECURITY_CONFIG.PASSWORD_REQUIRE_LOWERCASE && !/[a-z]/.test(pwd)) {
        return false;
      }
      // Check for number
      if (SECURITY_CONFIG.PASSWORD_REQUIRE_NUMBERS && !/\d/.test(pwd)) {
        return false;
      }
      // Check for special character
      if (SECURITY_CONFIG.PASSWORD_REQUIRE_SYMBOLS && !PASSWORD_SYMBOL_REGEX.test(pwd)) {
        return false;
      }
      // Check for common weak passwords
      const commonPasswords = ['password', '123456', 'admin', 'qwerty', 'letmein', 'welcome'];
      if (commonPasswords.some(common => pwd.toLowerCase().includes(common))) {
        return false;
      }
      return true;
    }, 'Password does not meet security requirements'),

  // URL validation with security checks
  url: z.string()
    .min(1, 'URL is required')
    .max(SECURITY_CONFIG.MAX_URL_LENGTH, 'URL too long')
    .refine((url) => {
      // Must start with http:// or https://
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return false;
      }
      // Check for suspicious protocols
      if (url.includes('javascript:') || url.includes('data:') || url.includes('vbscript:')) {
        return false;
      }
      // Basic URL format validation
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    }, 'Invalid URL format')
    .transform(sanitizeInput),

  // Text content validation
  text: z.string()
    .max(SECURITY_CONFIG.MAX_TEXT_LENGTH, 'Text content too long')
    .refine((text) => {
      // Check for suspicious patterns
      const suspiciousPatterns = [
        /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
        /javascript:/gi,
        /on\w+\s*=/gi,
        /<iframe/gi,
        /<object/gi,
        /<embed/gi
      ];
      
      return !suspiciousPatterns.some(pattern => pattern.test(text));
    }, 'Content contains potentially unsafe elements')
    .transform(sanitizeInput),

  // String validation for general text
  string: z.string()
    .max(SECURITY_CONFIG.MAX_STRING_LENGTH, 'Text too long')
    .refine((str) => {
      // Check for SQL injection patterns
      const sqlPatterns = [
        /\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b/gi,
        /--/g,
        /\/\*/g,
        /\*\//g
      ];
      
      return !sqlPatterns.some(pattern => pattern.test(str));
    }, 'Content contains potentially unsafe patterns')
    .transform(sanitizeInput),

  // File name validation
  filename: z.string()
    .min(1, 'Filename is required')
    .max(255, 'Filename too long')
    .refine((name) => {
      // Check for path traversal
      if (name.includes('..') || name.includes('/') || name.includes('\\')) {
        return false;
      }
      // Check for suspicious extensions
      const suspiciousExts = ['.exe', '.bat', '.cmd', '.scr', '.pif', '.com', '.vbs', '.js', '.jar', '.php', '.asp', '.jsp'];
      const ext = name.toLowerCase().split('.').pop();
      return !suspiciousExts.some(suspicious => suspicious.endsWith(`.${ext}`));
    }, 'Invalid filename')
    .transform(sanitizeInput),

  // Phone number validation
  phone: z.string()
    .min(10, 'Phone number too short')
    .max(20, 'Phone number too long')
    .refine((phone) => {
      // Allow only digits, spaces, hyphens, parentheses, and plus sign
      return /^[\d\s()+-]+$/.test(phone);
    }, 'Invalid phone number format')
    .transform(sanitizeInput),

  // Date validation
  date: z.string()
    .refine((date) => {
      const parsedDate = new Date(date);
      return !isNaN(parsedDate.getTime());
    }, 'Invalid date format')
    .refine((date) => {
      const parsedDate = new Date(date);
      const now = new Date();
      const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      const oneYearFromNow = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
      return parsedDate >= oneYearAgo && parsedDate <= oneYearFromNow;
    }, 'Date must be within a reasonable range'),

  // Number validation with range checks
  number: z.number()
    .min(0, 'Number must be positive')
    .max(Number.MAX_SAFE_INTEGER, 'Number too large')
    .refine((num) => Number.isFinite(num), 'Number must be finite'),

  // Integer validation
  integer: z.number()
    .int('Must be an integer')
    .min(0, 'Must be positive')
    .max(Number.MAX_SAFE_INTEGER, 'Number too large'),

  // Boolean validation
  boolean: z.boolean(),

  // Array validation with length limits
  array: z.array(z.any())
    .max(100, 'Array too large'),

  // Object validation
  object: z.object({}).passthrough()
    .refine((obj) => {
      // Check for prototype pollution
      return !Object.prototype.hasOwnProperty.call(obj, '__proto__') &&
             !Object.prototype.hasOwnProperty.call(obj, 'constructor') &&
             !Object.prototype.hasOwnProperty.call(obj, 'prototype');
    }, 'Object contains potentially unsafe properties'),
} as const;

/**
 * Form validation schemas for specific forms
 */
export const formSchemas = {
  // Admin login form
  adminLogin: z.object({
    email: validationSchemas.email,
    password: validationSchemas.password,
  }),

  // Event form
  event: z.object({
    title: validationSchemas.string,
    date: validationSchemas.date,
    time: z.string().max(10, 'Time format invalid'),
    location: validationSchemas.string,
    category: validationSchemas.string,
    description: validationSchemas.text,
    capacity: validationSchemas.integer,
    featured: validationSchemas.boolean,
    published: validationSchemas.boolean,
    featured_image: validationSchemas.url.optional(),
    registration_link: validationSchemas.url.optional(),
    contact_email: validationSchemas.email.optional(),
    contact_phone: validationSchemas.phone.optional(),
    admission_fee: validationSchemas.number.optional(),
  }),

  // News article form
  newsArticle: z.object({
    title: validationSchemas.string,
    excerpt: validationSchemas.text,
    date: validationSchemas.date,
    category: validationSchemas.string,
    author: validationSchemas.string,
    image: validationSchemas.url.optional(),
    featured: validationSchemas.boolean,
    published: validationSchemas.boolean,
    content: validationSchemas.text.optional(),
    meta_description: validationSchemas.string.optional(),
    tags: z.array(validationSchemas.string).max(10, 'Too many tags'),
  }),

  // Photo album form
  photoAlbum: z.object({
    title: validationSchemas.string,
    description: validationSchemas.text.optional(),
    category: validationSchemas.string,
    cover_image: validationSchemas.url.optional(),
    published: validationSchemas.boolean,
    order_index: validationSchemas.integer,
  }),

  // Photo form
  photo: z.object({
    album_id: validationSchemas.integer,
    url: validationSchemas.url,
    caption: validationSchemas.text.optional(),
    description: validationSchemas.text.optional(),
    order_index: validationSchemas.integer,
    published: validationSchemas.boolean,
  }),

  // Sponsor form
  sponsor: z.object({
    organization_name: validationSchemas.string,
    logo_url: validationSchemas.url.optional(),
    website_url: validationSchemas.url.optional(),
    description: validationSchemas.text.optional(),
    sponsor_level: validationSchemas.string,
    contact_information: validationSchemas.text.optional(),
    active: validationSchemas.boolean,
  }),

  // Membership application form
  membershipApplication: z.object({
    first_name: validationSchemas.string,
    last_name: validationSchemas.string,
    email: validationSchemas.email,
    phone: validationSchemas.phone,
    organization: validationSchemas.string.optional(),
    membership_type: validationSchemas.string.optional(),
    membership_length: validationSchemas.string.optional(),
    notes: validationSchemas.text.optional(),
  }),

  // Page form
  page: z.object({
    title: validationSchemas.string,
    slug: z.string()
      .min(1, 'Slug is required')
      .max(100, 'Slug too long')
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be lowercase with hyphens')
      .transform(sanitizeInput),
    description: validationSchemas.text.optional(),
    seo_title: validationSchemas.string.optional(),
    seo_description: validationSchemas.text.optional(),
    seo_keywords: z.array(validationSchemas.string).max(20, 'Too many keywords'),
    is_published: validationSchemas.boolean,
    content: validationSchemas.object,
  }),
} as const;

/**
 * Validate and sanitize form data
 * @param data - Form data to validate
 * @param schema - Zod schema to validate against
 * @returns Validation result
 */
export const validateFormData = <T>(
  data: unknown,
  schema: z.ZodSchema<T>
): { success: boolean; data?: T; errors?: Record<string, string[]> } => {
  try {
    const result = schema.safeParse(data);
    
    if (result.success) {
      return { success: true, data: result.data };
    } else {
      const errors: Record<string, string[]> = {};
      
      result.error.errors.forEach((error) => {
        const path = error.path.join('.');
        if (!errors[path]) {
          errors[path] = [];
        }
        errors[path].push(error.message);
      });
      
      return { success: false, errors };
    }
  } catch (error) {
    console.error('Validation error:', error);
    return { 
      success: false, 
      errors: { general: ['Validation failed'] } 
    };
  }
};

/**
 * Sanitize HTML content for safe display
 * @param html - HTML content to sanitize
 * @returns Sanitized HTML
 */
import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content using DOMPurify
 * Provides comprehensive XSS protection
 */
export const sanitizeHtml = (html: string): string => {
  if (typeof html !== 'string') return '';
  
  // Use DOMPurify for comprehensive sanitization
  return DOMPurify.sanitize(html, {
    // Allowed HTML tags for rich text content
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'strike', 'del', 'ins',
      'a', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'img', 'figure', 'figcaption',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'div', 'span', 'hr', 'sub', 'sup'
    ],
    // Allowed attributes
    ALLOWED_ATTR: [
      'href', 'title', 'alt', 'src', 'width', 'height',
      'class', 'id', 'target', 'rel', 'data-*'
    ],
    // Forbid dangerous tags
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    // Forbid dangerous attributes
    FORBID_ATTR: [
      'onerror', 'onclick', 'onload', 'onmouseover', 'onfocus',
      'onblur', 'onchange', 'onsubmit', 'onreset'
    ],
    // Additional security options
    ALLOW_DATA_ATTR: true,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false,
    // Sanitize URLs
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
  });
};

/**
 * Validate file upload with security checks
 * @param file - File to validate
 * @param allowedTypes - Allowed MIME types
 * @param maxSize - Maximum file size in bytes
 * @returns Validation result
 */
export const validateFileUpload = (
  file: File,
  allowedTypes: string[] = SECURITY_CONFIG.ALLOWED_IMAGE_TYPES,
  maxSize: number = SECURITY_CONFIG.MAX_FILE_SIZE
): { valid: boolean; error?: string } => {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }
  
  // Validate file has required properties
  if (!file.name || typeof file.name !== 'string') {
    return { valid: false, error: 'Invalid file: missing filename' };
  }
  
  if (file.size === undefined || file.size === null) {
    return { valid: false, error: 'Invalid file: missing file size' };
  }
  
  // Get file extension as fallback if MIME type is missing or incorrect
  const fileExt = file.name.split('.').pop()?.toLowerCase();
  const extensionToMime: Record<string, string[]> = {
    'jpg': ['image/jpeg', 'image/jpg'],
    'jpeg': ['image/jpeg', 'image/jpg'],
    'png': ['image/png'],
    'webp': ['image/webp'],
    'gif': ['image/gif'],
  };
  
  // Check file type - try MIME type first, then extension
  const mimeTypeValid = allowedTypes.includes(file.type);
  const extensionValid = fileExt && extensionToMime[fileExt] 
    ? extensionToMime[fileExt].some(mime => allowedTypes.includes(mime))
    : false;
  
  if (!mimeTypeValid && !extensionValid) {
    return { valid: false, error: `Invalid file type. Allowed types: ${allowedTypes.join(', ')}` };
  }
  
  // Check file size
  if (file.size > maxSize) {
    return { valid: false, error: `File too large. Maximum size: ${Math.round(maxSize / 1024 / 1024)}MB` };
  }
  
  // Check file name for suspicious patterns
  const suspiciousPatterns = /\.(exe|bat|cmd|scr|pif|com|vbs|js|jar|php|asp|jsp)$/i;
  if (suspiciousPatterns.test(file.name)) {
    return { valid: false, error: 'File type not allowed for security reasons' };
  }
  
  // Check for path traversal in filename
  if (file.name.includes('..') || file.name.includes('/') || file.name.includes('\\')) {
    return { valid: false, error: 'Invalid file name' };
  }
  
  return { valid: true };
};




